import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { CheckCircle2, Camera, X, Undo2, ChevronDown, ChevronRight, Lock, MessageSquare } from "lucide-react";

const fmtDateTime = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

const STATUS_ORDER = { not_started: 0, complete: 1, closed_out: 2 };
const fmtDate = (iso) => { if (!iso) return null; try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); } catch { return null; } };
const effProgress = (it) => (it.status === "complete" || it.status === "closed_out") ? 100 : (it.progress || 0);
const pctComplete = (items) => items.length ? Math.round(items.reduce((s, i) => s + effProgress(i), 0) / items.length) : 0;

function PortalNotes({ item, onAdd }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const notes = item.notes || [];
  const canAdd = item.status === "not_started";
  if (notes.length === 0 && !canAdd) return null;
  return (
    <div className="mt-2 pt-2 border-t">
      <button className={`flex items-center gap-1 text-xs font-semibold ${notes.length ? "text-amber-600" : "text-slate-500"}`}
        onClick={() => setOpen(!open)} data-testid={`portal-notes-toggle-${item.id}`}>
        <MessageSquare className="w-3.5 h-3.5" />
        {notes.length > 0 ? `Notes (${notes.length})` : "Add a note"}
      </button>
      {open && (
        <div className="mt-2 space-y-2" data-testid={`portal-notes-panel-${item.id}`}>
          {notes.map((n) => (
            <div key={n.id} className={`border rounded-md p-2 ${n.role === "manager" ? "border-blue-300 bg-blue-50" : "border-emerald-300 bg-emerald-50"}`}>
              <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{n.text}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${n.role === "manager" ? "bg-blue-200 text-blue-800" : "bg-emerald-200 text-emerald-800"}`}>{n.role === "manager" ? "Site Manager" : "You"}</span>
                <span className="text-xs text-slate-500">{n.by} · {fmtDateTime(n.at)}</span>
              </div>
            </div>
          ))}
          {canAdd && (
            <div className="space-y-1">
              <textarea maxLength={250} rows={2} className="w-full rounded-md border border-slate-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                placeholder="Add a note (max 250 characters)…" value={text} onChange={(e) => setText(e.target.value)} data-testid={`portal-note-input-${item.id}`} />
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">{text.length}/250</span>
                <Button size="sm" disabled={!text.trim()} data-testid={`portal-note-submit-${item.id}`}
                  onClick={async () => { if (await onAdd(item.id, text.trim())) setText(""); }}>Add note</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SiteTotals({ items }) {
  const total = items.length;
  const pc = pctComplete(items);
  const aw = items.filter((i) => i.status === "complete").length;
  const co = items.filter((i) => i.status === "closed_out").length;
  const cell = "flex-1 min-w-[68px] rounded-lg px-2 py-2 text-center";
  return (
    <div className="mb-3 rounded-xl border bg-white p-3" data-testid="portal-site-totals">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Whole-site totals</p>
      <div className="flex flex-wrap gap-2">
        <div className={`${cell} bg-slate-100`}><div className="text-lg font-bold text-slate-800">{total}</div><div className="text-[11px] text-slate-500">Total</div></div>
        <div className={`${cell} bg-blue-50`}><div className="text-lg font-bold text-blue-700">{pc}%</div><div className="text-[11px] text-blue-700/80">Complete</div></div>
        <div className={`${cell} bg-green-50`}><div className="text-lg font-bold text-green-700">{aw}</div><div className="text-[11px] text-green-700/80">Completed</div></div>
        <div className={`${cell} bg-slate-200`}><div className="text-lg font-bold text-slate-700">{co}</div><div className="text-[11px] text-slate-600">Closed out</div></div>
      </div>
      <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-500 transition-all" style={{ width: `${pc}%` }} /></div>
    </div>
  );
}

function CountBadges({ items }) {
  const total = items.length;
  const pc = pctComplete(items);
  const cp = items.filter((i) => i.status === "complete").length;
  const co = items.filter((i) => i.status === "closed_out").length;
  const pill = "text-[10px] leading-none px-1.5 py-1 rounded-full font-semibold whitespace-nowrap";
  return (
    <span className="flex items-center gap-1 flex-wrap" data-testid="portal-count-badges">
      <span className={`${pill} bg-slate-200 text-slate-700`}>{total} total</span>
      <span className={`${pill} bg-blue-100 text-blue-700`}>{pc}% complete</span>
      <span className={`${pill} bg-green-100 text-green-700`}>{cp} completed</span>
      <span className={`${pill} bg-slate-300 text-slate-700`}>{co} closed out</span>
    </span>
  );
}

export default function FitOffPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [revoked, setRevoked] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [active, setActive] = useState(null); // item being completed
  const [expandedTh, setExpandedTh] = useState({});   // collapsed by default
  const [expandedRoom, setExpandedRoom] = useState({});

  const load = async () => {
    const res = await fetch(`${API}/public/fitoff/${token}`);
    if (res.status === 403) { setRevoked(true); return; }
    if (res.ok) setData(await res.json());
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const undo = async (id) => {
    const res = await fetch(`${API}/public/fitoff/${token}/items/${id}/undo`, { method: "POST" });
    if (res.ok) { toast.success("Undone"); load(); }
  };
  const setProgress = async (id, pct) => {
    const res = await fetch(`${API}/public/fitoff/${token}/items/${id}/progress`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ progress: Number(pct) }) });
    if (res.ok) load(); else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };
  const addNote = async (id, text) => {
    const res = await fetch(`${API}/public/fitoff/${token}/items/${id}/notes`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
    if (res.ok) { toast.success("Note added"); load(); return true; }
    const e = await res.json(); toast.error(e.detail || "Failed"); return false;
  };

  if (revoked) return <Centered><h1 className="text-xl font-bold text-slate-800">Link no longer active</h1><p className="text-slate-500 mt-2">This link is no longer active. Please contact your builder.</p></Centered>;
  if (!data) return <Centered><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" /></Centered>;

  const grouped = {};
  (data.items || []).forEach((i) => {
    const th = i.townhouse_label || "Lot";
    const room = i.room_instance_label || i.room_name || "Other";
    grouped[th] = grouped[th] || {};
    (grouped[th][room] = grouped[th][room] || []).push(i);
  });
  const sortNat = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#0D1B2A] text-white px-4 py-4 sticky top-0 z-10 border-b-4 border-orange-500">
        <div className="flex items-center gap-2">
          <img src="/logo.png?v=3" alt="SiteProApp" className="h-8 w-8" />
          <div>
            <h1 className="font-bold text-lg leading-tight">{data.subcontractor_name} — {data.project_name}</h1>
            <p className="text-xs text-slate-300">Tap an item to mark it complete</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-3">
        {(data.items || []).length > 0 && <SiteTotals items={data.items} />}
        {(() => { const n = (data.items || []).filter((i) => i.ready_to_start && i.status === "not_started").length;
          return n > 0 ? <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm font-semibold text-emerald-800" data-testid="ready-banner">✅ {n} item{n !== 1 ? "s" : ""} ready to start now</div> : null; })()}
        {Object.keys(grouped).length === 0 && <p className="text-center text-slate-400 mt-10">No items yet.</p>}

        {Object.keys(grouped).sort(sortNat).map((th) => {
          const thOpen = !!expandedTh[th];
          const thItems = Object.values(grouped[th]).reduce((a, r) => a.concat(r), []);
          return (
            <div key={th} className="mb-2 bg-white rounded-lg border overflow-hidden">
              <button className="w-full flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 p-3 text-left font-bold text-slate-800" data-testid={`portal-dwelling-${th}`}
                onClick={() => setExpandedTh({ ...expandedTh, [th]: !thOpen })}>
                <span className="flex items-center gap-2 w-full sm:flex-1 min-w-0">
                  {thOpen ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                  <span className="min-w-0 truncate">{th}</span>
                </span>
                <span className="pl-6 sm:pl-0"><CountBadges items={thItems} /></span>
              </button>
              {thOpen && (
                <div className="border-t">
                  {Object.keys(grouped[th]).sort(sortNat).map((room) => {
                    const rk = `${th}||${room}`;
                    const roomOpen = !!expandedRoom[rk];
                    return (
                      <div key={rk} className="border-b last:border-0">
                        <button className="w-full flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 px-3 py-2 text-left bg-slate-50" data-testid={`portal-room-${rk}`}
                          onClick={() => setExpandedRoom({ ...expandedRoom, [rk]: !roomOpen })}>
                          <span className="flex items-center gap-2 w-full sm:flex-1 min-w-0">
                            {roomOpen ? <ChevronDown className="w-4 h-4 shrink-0 text-slate-500" /> : <ChevronRight className="w-4 h-4 shrink-0 text-slate-500" />}
                            <span className="min-w-0 truncate text-sm font-semibold text-slate-700">{room}</span>
                          </span>
                          <span className="pl-6 sm:pl-0"><CountBadges items={grouped[th][room]} /></span>
                        </button>
                        {roomOpen && (
                          <div className="p-2 space-y-2">
                            {[...grouped[th][room]].sort((a, b) => (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || sortNat(a.item_name || "", b.item_name || "")).map((it) => {
                              const doneDate = fmtDate(it.marked_complete_at);
                              return (
                              <div key={it.id} className="bg-white rounded-lg border p-3" data-testid={`portal-item-${it.id}`}>
                                <div className="flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-900">{it.item_name}</p>
                                  {it.status !== "closed_out" && it.status !== "complete" && it.ready_to_start && (
                                    <span className="inline-block mt-0.5 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold" data-testid={`ready-badge-${it.id}`}>✅ Ready to start</span>
                                  )}
                                  {doneDate && <p className="text-xs text-slate-500 mt-0.5">Completed {doneDate}{it.marked_complete_by ? ` · ${it.marked_complete_by}` : ""}</p>}
                                </div>
                                {it.status === "closed_out" ? (
                                  <span className="inline-flex items-center gap-1 text-slate-500 text-sm font-medium whitespace-nowrap"><Lock className="w-4 h-4" />Closed Out</span>
                                ) : it.status === "complete" ? (
                                  <div className="flex items-center gap-2">
                                    <span className="inline-flex items-center gap-1 text-green-600 text-sm font-medium"><CheckCircle2 className="w-4 h-4" />Complete</span>
                                    <Button size="sm" variant="ghost" onClick={() => undo(it.id)} data-testid={`undo-${it.id}`}><Undo2 className="w-4 h-4" /></Button>
                                  </div>
                                ) : (
                                  <select className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
                                    data-testid={`progress-${it.id}`} value={String(it.progress || 0)}
                                    onChange={(e) => { const v = e.target.value; if (v === "100") { setActive(it); setShowComplete(true); } else setProgress(it.id, v); }}>
                                    <option value="0">0%</option>
                                    <option value="25">25%</option>
                                    <option value="50">50%</option>
                                    <option value="75">75%</option>
                                    <option value="100">Mark Complete (100%)</option>
                                  </select>
                                )}
                                </div>
                                <PortalNotes item={it} onAdd={addNote} />
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showComplete && active && (
        <CompleteSheet token={token} item={active}
          onClose={() => { setShowComplete(false); setActive(null); }}
          onDone={() => { setShowComplete(false); setActive(null); load(); }} />
      )}
    </div>
  );
}

function CompleteSheet({ token, item, onClose, onDone }) {
  const [name, setName] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const pick = (e) => {
    const arr = Array.from(e.target.files || []).slice(0, 3 - files.length);
    setFiles([...files, ...arr].slice(0, 3));
  };
  const submit = async () => {
    if (!name.trim()) return toast.error("Please enter your name");
    setBusy(true);
    const fd = new FormData();
    fd.append("marked_complete_by", name.trim());
    files.forEach((f) => fd.append("files", f));
    const res = await fetch(`${API}/public/fitoff/${token}/items/${item.id}/complete`, { method: "POST", body: fd });
    setBusy(false);
    if (res.ok) { toast.success("Marked complete"); onDone(); } else toast.error("Failed — try again");
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="complete-sheet">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-slate-900">{item.item_name}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-xs text-slate-500 mb-3">{item.townhouse_label} · {item.room_instance_label || item.room_name}</p>
        <label className="text-sm font-medium text-slate-700">Your name</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Who's completing this?" data-testid="complete-name" className="mb-3" />
        <label className="text-sm font-medium text-slate-700">Photos (optional, up to 3)</label>
        <div className="flex gap-2 my-2 flex-wrap">
          {files.map((f, i) => (
            <div key={i} className="relative">
              <img src={URL.createObjectURL(f)} alt="" className="w-16 h-16 object-cover rounded border" />
              <button className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5" onClick={() => setFiles(files.filter((_, x) => x !== i))}><X className="w-3 h-3" /></button>
            </div>
          ))}
          {files.length < 3 && (
            <label className="w-16 h-16 border-2 border-dashed rounded flex items-center justify-center cursor-pointer text-slate-400" data-testid="complete-add-photo">
              <Camera className="w-6 h-6" />
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
            </label>
          )}
        </div>
        <Button className="w-full bg-orange-500 hover:bg-orange-600 mt-3" disabled={busy} onClick={submit} data-testid="complete-confirm">
          {busy ? "Saving..." : "Confirm Complete"}
        </Button>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-slate-50">{children}</div>;
}
